import { vec3 } from "gl-matrix";
import { wHitRecord } from "./wHitRecord.js";
import { wPrim, wPrimType } from "./wPrim.js"

class wSphere extends wPrim {
    constructor(centerPosition, radius, lats, longs) {
        super("sphere", wPrimType.wPrimSphere);
        this.center_ = (centerPosition !== undefined) ? centerPosition : vec3.fromValues(0.0, 0.0, 0.0);
        this.radius_ = (radius !== undefined) ? radius : 1.0;
        this.lats_ = (lats !== undefined) ? lats : 12;
        this.longs_ = (longs !== undefined) ? longs : 12;
    }

    setCenterPosition(pos) {
        vec3.copy(this.center_, pos);
    }

    getRadius() {
        return this.radius_;
    }

    getCenterPoint() {
        var P = vec3.create();
        vec3.copy(P, this.center_);
        return P;
    }

    rayIntersect(ray) {
        var hitRecord = new wHitRecord();

        let r = this.radius_;
        let c = this.center_;
        let r0 = ray.getOrigin();
        let rD = ray.getDirection();

        let tmp = vec3.create();
        vec3.subtract(tmp, c, r0); // tmp: line from ray origin to center of sphere
        let t = vec3.dot(tmp, rD);

        vec3.scale(tmp, rD, t); // tmp: rD*t

        let p = vec3.create();
        vec3.add(p, tmp, r0); // p = tmp +r0 (closest point on ray to center of sphere)

        vec3.sub(tmp, c, p); // tmp: vector from closest point on ray to center of sphere
        let y = vec3.length(tmp);

        if (y < r) {
            let x = Math.sqrt(r * r - y * y);
            hitRecord.hitTimes_.push(t - x);
            hitRecord.hitTimes_.push(t + x);
            hitRecord.validHit_ = true;
        }
        return hitRecord;
    }

    getTriangulation() {
        let lats = this.lats_;
        let longs = this.longs_;
        let r = this.radius_;
        let c = this.center_;
        let verts = [];
        let trianglution = [];

        let index = 0;
        verts[index] = vec3.fromValues(c[0], 1.0 * r + c[1], c[2]);
        index++;
        for (let j = 0; j < longs - 1; ++j) {
            let polar = (Math.PI * (j + 1)) / longs;
            let sp = Math.sin(polar);
            let cp = Math.cos(polar);
            for (let i = 0; i < lats; ++i) {
                let azimuth = (2.0 * Math.PI * i) / lats;
                let sa = Math.sin(azimuth);
                let ca = Math.cos(azimuth);
                let x = sp * ca;
                let y = cp;
                let z = sp * sa;
                verts[index] = vec3.fromValues(r * x + c[0], r * y + c[1], r * z + c[2]);
                index++;
            }
        }
    
        verts[index] = vec3.fromValues(c[0], -1.0 * r + c[1], c[2]);

        for (let i = 0; i < lats; ++i) {
            let a = i + 1;
            let b = ((i + 1) % lats) + 1;
            trianglution.push(verts[0]);
            trianglution.push(verts[b]);
            trianglution.push(verts[a]);
        }

        for (let j = 0; j < longs - 2; ++j) {
            let aStart = j * lats + 1;
            let bStart = (j + 1) * lats + 1;
            for (let i = 0; i < lats; ++i) {
                let a = aStart + i;
                let a1 = aStart + ((i + 1) % lats);
                let b = bStart + i;
                let b1 = bStart + ((i + 1) % lats);

                trianglution.push(verts[a1]);
                trianglution.push(verts[b1]);
                trianglution.push(verts[b]);

                trianglution.push(verts[a]);
                trianglution.push(verts[a1]);
                trianglution.push(verts[b]);
            }
        }

        for (let i = 0; i < lats; ++i) {
            let a = i + lats * (longs - 2) + 1;
            let b = ((i + 1) % lats) + lats * (longs - 2) + 1;
            trianglution.push(verts[verts.length - 1]);
            trianglution.push(verts[a]);
            trianglution.push(verts[b]);
        }

        return trianglution;
    }
}

export { wSphere };
